/**
 * LogoCropperModal - Ladda upp, beskär och förhandsgranska organisationslogotyp
 * Funktioner:
 *  - Välj bild (valfri storlek, beskärs lokalt)
 *  - Fri beskärning med react-image-crop
 *  - Välja transparent eller vit/mörk bakgrund
 *  - Förhandsgranska i light och dark mode
 *  - Exportera som PNG via canvas och ladda upp till API
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  XMarkIcon,
  SunIcon,
  MoonIcon,
  PhotoIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface LogoCropperModalProps {
  organizationName: string;
  onSave: (blob: Blob) => Promise<void>;
  onClose: () => void;
}

type BackgroundMode = 'transparent' | 'white' | 'dark';

const OUTPUT_MAX = 400; // px, max dimension vid export

function centerFreeCrop(mediaWidth: number, mediaHeight: number): Crop {
  // Fri beskärning - 85% av bilden centrerat, inga proportionslås
  const widthPct = 85;
  const heightPct = 85;
  const x = (mediaWidth * (100 - widthPct)) / 200;
  const y = (mediaHeight * (100 - heightPct)) / 200;
  return {
    unit: 'px',
    x,
    y,
    width: mediaWidth * widthPct / 100,
    height: mediaHeight * heightPct / 100,
  };
}

async function cropImageToBlob(
  image: HTMLImageElement,
  crop: PixelCrop,
  background: BackgroundMode
): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const srcW = crop.width * scaleX;
  const srcH = crop.height * scaleY;
  const aspect = srcW / srcH;

  // Beräkna utdatastorlek med bibehållna proportioner, max OUTPUT_MAX px
  let outW: number, outH: number;
  if (aspect >= 1) {
    outW = OUTPUT_MAX;
    outH = Math.round(OUTPUT_MAX / aspect);
  } else {
    outH = OUTPUT_MAX;
    outW = Math.round(OUTPUT_MAX * aspect);
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d')!;

  // Bakgrund
  if (background === 'white') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);
  } else if (background === 'dark') {
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, outW, outH);
  }
  // transparent = inget fill

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    srcW,
    srcH,
    0,
    0,
    outW,
    outH
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob misslyckades'));
      },
      'image/png',
      1
    );
  });
}

export const LogoCropperModal: React.FC<LogoCropperModalProps> = ({
  organizationName,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  // Städa upp objekt-URL:er vid avmontering
  useEffect(() => {
    return () => {
      if (imgSrc.startsWith('blob:')) URL.revokeObjectURL(imgSrc);
    };
  }, [imgSrc]);
  const [background, setBackground] = useState<BackgroundMode>('transparent');
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('light');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewDebounce = useRef<ReturnType<typeof setTimeout>>();

  const loadFile = (file: File) => {
    // Revoke any previous object URL to avoid memory leaks
    if (imgSrc.startsWith('blob:')) URL.revokeObjectURL(imgSrc);
    const url = URL.createObjectURL(file);
    setImgSrc(url);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setPreviewUrl('');
  };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) loadFile(e.target.files[0]);
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerFreeCrop(width, height));
  }, []);

  // Uppdatera förhandsvisning när crop eller bakgrund ändras
  useEffect(() => {
    clearTimeout(previewDebounce.current);
    if (!completedCrop || !imgRef.current || completedCrop.width === 0) {
      setPreviewUrl('');
      return;
    }
    previewDebounce.current = setTimeout(async () => {
      try {
        const blob = await cropImageToBlob(imgRef.current!, completedCrop, background);
        const url = URL.createObjectURL(blob);
        setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      } catch {
        // ignore
      }
    }, 150);
    return () => clearTimeout(previewDebounce.current);
  }, [completedCrop, background]);

  const handleSave = async () => {
    if (!completedCrop || !imgRef.current) return;
    setSaving(true);
    try {
      const blob = await cropImageToBlob(imgRef.current, completedCrop, background);
      await onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) loadFile(file);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bgContainer rounded-xl border border-border w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-textDefault">Ladda upp logotyp</h2>
            <p className="text-sm text-textSecondary">{organizationName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-bgElevated rounded-lg">
            <XMarkIcon className="w-5 h-5 text-textSecondary" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {!imgSrc ? (
            /* Dropzon */
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-4 border-2 border-dashed rounded-xl p-16 cursor-pointer transition-colors
                ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-bgElevated'}`}
            >
              <PhotoIcon className="w-16 h-16 text-textTertiary" />
              <div className="text-center">
                <p className="text-textDefault font-medium">{t('copy.ui.logoDropImage')}</p>
                <p className="text-sm text-textSecondary mt-1">{t('copy.ui.logoClickToChoose')}</p>
                <p className="text-xs text-textTertiary mt-2">PNG, JPG, WebP, SVG, GIF — valfri storlek</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onSelectFile}
              />
            </div>
          ) : (
            <div className="flex gap-6 flex-wrap lg:flex-nowrap">

              {/* Vänster: Beskärning */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-textDefault">{t('copy.ui.logoCropImage')}</h3>
                  <button
                    onClick={() => { setImgSrc(''); setPreviewUrl(''); }}
                    className="text-xs text-textSecondary hover:text-primary"
                  >
                    {t('copy.ui.logoChooseOther')}
                  </button>
                </div>

                <div
                  className="rounded-lg border border-border overflow-auto"
                  style={{
                    maxHeight: '420px',
                    background: 'repeating-conic-gradient(#aaa 0% 25%, #eee 0% 50%) 0 0 / 16px 16px',
                  }}
                >
                  <ReactCrop
                    crop={crop}
                    onChange={(c: PixelCrop) => setCrop(c)}
                    onComplete={(c: PixelCrop) => setCompletedCrop(c)}
                    keepSelection
                    minWidth={20}
                    minHeight={20}
                  >
                    <img
                      ref={imgRef}
                      src={imgSrc}
                      alt={t('copy.ui.logoAltCrop')}
                      onLoad={onImageLoad}
                      style={{ maxWidth: '100%', maxHeight: '400px' }}
                    />
                  </ReactCrop>
                </div>

                {/* Bakgrundsval */}
                <div className="mt-4">
                  <p className="text-sm font-medium text-textDefault mb-2">{t('copy.ui.logoExportBackground')}</p>
                  <div className="flex gap-2">
                    {([
                      { value: 'transparent', label: 'Transparent', preview: 'bg-checkered' },
                      { value: 'white', label: t('copy.ui.logoBgWhite') },
                      { value: 'dark', label: t('copy.ui.logoBgDark') },
                    ] as { value: BackgroundMode; label: string; preview?: string }[]).map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setBackground(opt.value)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors
                          ${background === opt.value
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-textSecondary hover:border-primary/50'
                          }`}
                      >
                        <span
                          className="w-4 h-4 rounded border border-border flex-shrink-0"
                          style={{
                            background: opt.value === 'white' ? '#ffffff'
                              : opt.value === 'dark' ? '#1a1a2e'
                              : 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0 / 8px 8px',
                          }}
                        />
                        {opt.label}
                        {background === opt.value && <CheckIcon className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Höger: Förhandsvisning */}
              <div className="w-72 flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-textDefault">{t('copy.ui.logoPreview')}</h3>
                  <div className="flex gap-1 p-1 bg-bgElevated rounded-lg border border-border">
                    <button
                      onClick={() => setPreviewMode('light')}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                        ${previewMode === 'light' ? 'bg-white text-gray-800 shadow-sm' : 'text-textSecondary hover:text-textDefault'}`}
                    >
                      <SunIcon className="w-3.5 h-3.5" />
                      {t('copy.ui.logoLightPreview')}
                    </button>
                    <button
                      onClick={() => setPreviewMode('dark')}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors
                        ${previewMode === 'dark' ? 'bg-gray-900 text-white shadow-sm' : 'text-textSecondary hover:text-textDefault'}`}
                    >
                      <MoonIcon className="w-3.5 h-3.5" />
                      {t('copy.ui.logoDarkPreview')}
                    </button>
                  </div>
                </div>

                {/* Sidebar-preview */}
                <div
                  className="rounded-xl border border-border overflow-hidden"
                  style={{ background: previewMode === 'dark' ? '#0f172a' : '#f8fafc' }}
                >
                  {/* Simulerad sidebar-topp */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 border-b"
                    style={{
                      background: previewMode === 'dark' ? '#1e293b' : '#ffffff',
                      borderColor: previewMode === 'dark' ? '#334155' : '#e2e8f0',
                    }}
                  >
                    {previewUrl ? (
                      <img src={previewUrl} alt="Logo" className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-bgBase flex items-center justify-center">
                        <PhotoIcon className="w-4 h-4 text-textTertiary" />
                      </div>
                    )}
                    <span
                      className="text-sm font-semibold"
                      style={{ color: previewMode === 'dark' ? '#f1f5f9' : '#1e293b' }}
                    >
                      {organizationName}
                    </span>
                  </div>
                  {/* Simulerade menyrader */}
                  {[1, 2, 3].map(i => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-4 py-2.5"
                      style={{
                        borderBottom: `1px solid ${previewMode === 'dark' ? '#1e293b' : '#e2e8f0'}`,
                      }}
                    >
                      <div
                        className="w-4 h-4 rounded"
                        style={{ background: previewMode === 'dark' ? '#334155' : '#cbd5e1' }}
                      />
                      <div
                        className="h-2.5 rounded flex-1"
                        style={{
                          background: previewMode === 'dark' ? '#334155' : '#cbd5e1',
                          width: `${60 + i * 10}%`,
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Login-preview */}
                <p className="text-xs text-textSecondary mt-4 mb-2">Inloggningssidan</p>
                <div
                  className="rounded-xl border border-border p-6 flex flex-col items-center gap-3"
                  style={{ background: previewMode === 'dark' ? '#0f172a' : '#f8fafc' }}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="Logo" className="w-20 h-20 object-contain" />
                  ) : (
                    <div
                      className="w-20 h-20 rounded-xl flex items-center justify-center"
                      style={{ background: previewMode === 'dark' ? '#1e293b' : '#e2e8f0' }}
                    >
                      <PhotoIcon className="w-8 h-8 text-textTertiary" />
                    </div>
                  )}
                  <div
                    className="h-2 rounded w-32"
                    style={{ background: previewMode === 'dark' ? '#334155' : '#cbd5e1' }}
                  />
                  <div
                    className="h-8 rounded w-full"
                    style={{ background: previewMode === 'dark' ? '#1e293b' : '#e2e8f0' }}
                  />
                  <div
                    className="h-8 rounded w-full"
                    style={{ background: previewMode === 'dark' ? '#14b8a6' : '#0d9488' }}
                  />
                </div>

                {previewUrl && (
                  <p className="text-xs text-textTertiary mt-3 text-center">
                    {t('copy.ui.logoExportHint', { max: OUTPUT_MAX })}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgElevated text-sm"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={!completedCrop || completedCrop.width === 0 || saving}
            className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sparar...
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4" />
                Spara logotyp
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoCropperModal;
